import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuthReady } from '../../hooks/useAuthReady';
import { cardTxQueryOpts } from '../../hooks/useCards';
import { withdrawBananaOk } from '../../hooks/useWithdrawSettlements';
import { fetchCardIntents } from '../../lib/cards';
import { dbg } from '../../lib/debug';
import { TransactionRow, cardTxToTx, type CardTxDto } from './TransactionRow';

// Keep the initial DOM bounded. The API currently returns up to 50 records.
const PAGE = 5;

function mergeOptimisticWithdraws(real: CardTxDto[], optimistic: CardTxDto[]): CardTxDto[] {
  if (optimistic.length === 0) return real;
  const out = [...real];
  for (const o of optimistic) {
    const amt = Math.abs(Number(o.amount));
    const dup = real.some((r) => {
      if (!/withdraw/i.test(r.type)) return false;
      if (Math.abs(Math.abs(Number(r.amount)) - amt) > 0.011) return false;
      if (r.ts == null || o.ts == null) return true;
      return Math.abs(r.ts - o.ts) < 10 * 60_000;
    });
    if (!dup) out.unshift(o);
  }
  return out;
}

/** Live card transactions rendered in the same style as crypto wallet transactions. */
export function CardTransactions({ cardId, status }: { cardId?: string; status?: string }) {
  const { t, i18n } = useTranslation();
  const { canFetch: ready, initData, cacheUserId: userId } = useAuthReady();
  const [limit, setLimit] = useState(PAGE);

  // Reset the window when switching cards.
  useEffect(() => {
    setLimit(PAGE);
  }, [cardId]);

  const { data, isError, isLoading, isFetching, refetch } = useQuery({
    ...cardTxQueryOpts(initData, userId, cardId ?? ''),
    enabled: ready && !!cardId && status !== 'pending',
  });

  // Banana can lag after pull — show intent rows once stage is banana_ok+.
  const openWithdraws = useQuery({
    queryKey: ['card-intents', 'withdraw', 'open', userId],
    enabled: ready && !!cardId && status !== 'pending',
    queryFn: () => fetchCardIntents(initData, { kind: 'withdraw', status: 'open' }),
    refetchOnWindowFocus: true,
    refetchInterval: (query) => ((query.state.data?.intents?.length ?? 0) > 0 ? 1500 : false),
  });

  const optimistic: CardTxDto[] = (openWithdraws.data?.intents ?? [])
    .filter((i) => i.cardId === cardId && withdrawBananaOk(i) && i.amount != null)
    .map((i) => ({
      id: `intent:${i.id}`,
      type: 'withdraw',
      merchant: null,
      amount: String(i.amount),
      currency: 'USD',
      status: 'success',
      ts: new Date(i.createdAt).getTime(),
    }));

  const merged = mergeOptimisticWithdraws((data?.transactions ?? []) as CardTxDto[], optimistic);
  const txs = merged.map((tx) => cardTxToTx(tx, i18n.language, t));

  useEffect(() => {
    if (!isLoading) dbg(`CARDS tx n=${txs.length} shown=${Math.min(limit, txs.length)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, txs.length, limit]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-ink/15 border-t-ink" />
      </div>
    );
  }

  if (isError && !data) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <p className="text-[14px] text-muted">{t('cards.errProvider')}</p>
        <button
          type="button"
          disabled={isFetching}
          onClick={() => void refetch()}
          className="rounded-full bg-black/[0.05] px-5 py-2.5 text-[14px] font-medium text-ink active:bg-black/[0.08] disabled:opacity-50"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  if (txs.length === 0) {
    return <p className="py-10 text-center text-[14px] text-muted">{t('wallet.noTransactions')}</p>;
  }

  const shown = txs.slice(0, limit);
  const remaining = txs.length - shown.length;

  return (
    <div className="flex flex-col gap-2">
      {shown.map((tx, i) => (
        <TransactionRow key={i} tx={tx} light />
      ))}
      {remaining > 0 && (
        <button
          type="button"
          onClick={() => setLimit((l) => l + PAGE)}
          className="mt-1 self-center rounded-full bg-black/[0.05] px-5 py-2.5 text-[14px] font-medium text-ink active:bg-black/[0.08]"
        >
          {t('wallet.showMore', { defaultValue: 'Показать ещё' })}
        </button>
      )}
    </div>
  );
}
